import { Link } from 'react-router-dom'
import { CONTACT_EMAIL, TALYNTH_LIVE, TALYNTH_URL } from '../config/site'

const PRIVACY_EMAIL = 'privacy@stratumlabs.tech'

function Privacy() {
  return (
    <>
      <section className="hero hero--compact">
        <div className="container">
          <h1>Privacy policy</h1>
          <p className="tagline">
            How Stratum Labs (Pty) Ltd handles personal information collected through this website,
            under the Protection of Personal Information Act 4 of 2013 (POPIA).
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container container--narrow">
          <p className="price-card__tagline">Last updated: October 2026</p>

          <h2>1. Who we are</h2>
          <p>
            Stratum Labs (Pty) Ltd (&quot;Stratum Labs&quot;, &quot;we&quot;, &quot;us&quot;, or
            &quot;our&quot;) operates this website (<strong>stratumlabs.tech</strong>) and is the
            responsible party for the personal information it collects, as defined in POPIA.
          </p>

          <h2>2. What this policy covers</h2>
          <p>
            This policy covers only the personal information collected through{' '}
            <strong>this website</strong> — specifically, what you submit via the{' '}
            <Link to="/contact">Contact</Link> page. This site has no user accounts, no database,
            and runs no analytics or tracking cookies of any kind.
          </p>
          <p>
            If you become a <strong>Website-as-a-Service</strong> hosting customer, we cover that
            relationship directly in <strong>Section 7</strong> below, since we operate the hosting
            infrastructure ourselves. For a{' '}
            <strong>bespoke build, custom software development, or consulting</strong> engagement,
            data handling for that specific project is governed by the agreement for that engagement
            rather than this page — the right safeguards depend entirely on what the project
            actually involves, which we agree with you in writing before work begins (see Section 7
            for how this applies per service).
          </p>

          <h2>3. What we collect, and why</h2>
          <p>
            When you submit the Contact form, we collect: your <strong>name</strong>,{' '}
            <strong>email address</strong>, the <strong>enquiry category</strong> you select, and
            the <strong>message</strong> you write. We use this solely to respond to your enquiry
            and route it to the right part of the business. We do not collect anything else about
            you through this site — no device fingerprinting, no location data, no behavioural
            tracking.
          </p>

          <h2>4. How your enquiry is delivered — Web3Forms</h2>
          <p>
            This site has no backend of its own. Contact form submissions are sent directly from
            your browser to <strong>Web3Forms</strong>, a third-party form-delivery service, which
            forwards your enquiry to <strong>{CONTACT_EMAIL}</strong>. Based on Web3Forms&apos; own
            published documentation:
          </p>
          <ul className="feature-list">
            <li>Web3Forms does not store form submissions — it processes and forwards them.</li>
            <li>
              It may retain server logs containing personal information for a limited period
              (periodically deleted).
            </li>
            <li>
              Its servers are located in the <strong>United States</strong>, not South Africa —
              submitting the form is a cross-border transfer of your personal information for the
              purpose of delivering your enquiry to us.
            </li>
          </ul>
          <p>
            We do not control Web3Forms&apos; infrastructure directly; we rely on its published
            practices and its own compliance posture as a data processor acting on our instructions.
          </p>

          <h2>5. Legal basis for processing</h2>
          <p>
            We process the information you submit on the basis of your <strong>consent</strong>{' '}
            (given by submitting the form) and because it is <strong>necessary</strong> to respond
            to the enquiry you have raised.
          </p>

          <h2>6. Retention</h2>
          <p>
            We keep enquiry emails for as long as reasonably needed to respond to and follow up on
            your enquiry, and delete them once that purpose has been served, unless a longer period
            is needed for a legitimate business or legal reason (for example, records of an
            engagement that proceeds).
          </p>

          <h2>7. How this applies to each service we offer</h2>
          <p>
            The categories below mirror the Contact form&apos;s enquiry types. Each has a different
            data relationship — this page is deliberately specific rather than making one broad
            claim that doesn&apos;t fit all of them:
          </p>
          <ul className="feature-list">
            <li>
              <strong>Website-as-a-Service (Basic / Advanced):</strong> initial contact is handled
              as described above. If you become a hosting customer, we hold your business and
              billing details (name, business email, domain) for account administration. Your site
              itself runs on AWS infrastructure we operate (S3 + CloudFront) under our control as
              the host — if your site includes its own data collection (a contact form, or the
              Basic/Enhanced analytics listed on the <Link to="/services">Services</Link> page), we
              process that data as the host on your behalf, and you remain responsible for your own
              site&apos;s compliance with POPIA towards your visitors (for example, your site
              needing its own privacy notice). We do not access or use your site&apos;s visitor data
              for our own purposes.
            </li>
            <li>
              <strong>Custom website build / custom software development:</strong> scoping and
              delivery of a bespoke project is governed by that project&apos;s agreement. Data
              handling (what&apos;s collected, where it&apos;s stored, who else can access it) is
              defined per project to match its actual requirements — a small brochure site and a
              system handling sensitive customer data need very different safeguards, so we do not
              pretend otherwise with a one-size-fits-all promise here.
            </li>
            <li>
              <strong>Project consulting:</strong> typically involves reviewing material you provide
              (code, architecture, documents). Confidentiality and data handling for the engagement
              are agreed before any material is shared.
            </li>
            <li>
              <strong>Self-hosting / site ownership transfer:</strong> this is specifically about
              handing your data and infrastructure <em>to you</em> — the point of the engagement is
              to reduce what we hold, not increase it.
            </li>
            <li>
              <strong>Talynth:</strong> Talynth is a separate product with its own privacy policy,
              covering its own account data, assessments, and billing — see{' '}
              {TALYNTH_LIVE ? (
                <a className="link" href={TALYNTH_URL} target="_blank" rel="noreferrer">
                  talynth.tech
                </a>
              ) : (
                // Pre-launch: Talynth isn't live yet, so reference the domain as
                // plain text rather than a link to a not-yet-live site (gated by
                // TALYNTH_LIVE, same as the site's other talynth.tech links).
                <span>talynth.tech</span>
              )}
              . This page covers only this marketing site.
            </li>
            <li>
              <strong>General enquiry:</strong> handled as described in Sections 3–6 above, with
              nothing further.
            </li>
          </ul>

          <h2>8. Your rights under POPIA</h2>
          <p>You have the right to:</p>
          <ul className="feature-list">
            <li>Request confirmation of what personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request that we delete personal information we no longer need</li>
            <li>Object to our processing of your personal information</li>
            <li>
              Withdraw consent at any time (this does not affect the lawfulness of processing before
              withdrawal)
            </li>
          </ul>
          <p>
            To exercise any of these rights, contact us at{' '}
            <a className="link" href={`mailto:${PRIVACY_EMAIL}`}>
              {PRIVACY_EMAIL}
            </a>
            .
          </p>

          <h2>9. Children</h2>
          <p>
            This site is intended for business visitors and is not directed at children. We do not
            knowingly collect personal information from children through this site.
          </p>

          <h2>10. Changes to this policy</h2>
          <p>
            We may update this policy as the site or our practices change. The &quot;Last
            updated&quot; date above reflects the most recent revision.
          </p>

          <h2>11. Contact and complaints</h2>
          <p>
            For any privacy-related query or to exercise your rights, contact{' '}
            <a className="link" href={`mailto:${PRIVACY_EMAIL}`}>
              {PRIVACY_EMAIL}
            </a>
            . If you are not satisfied with our response, you may lodge a complaint with South
            Africa&apos;s Information Regulator:
          </p>
          <ul className="feature-list">
            <li>
              Email:{' '}
              <a className="link" href="mailto:complaints.IR@justice.gov.za">
                complaints.IR@justice.gov.za
              </a>
            </li>
            <li>Tel: 012 406 4818</li>
          </ul>
        </div>
      </section>
    </>
  )
}

export default Privacy
